import { Checkbox, Inline, Input, Stack } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import type { ChangeEvent, ComponentPropsWithoutRef, Ref } from 'react';
import { useCallback, useMemo } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';

type FormatParsePair =
  | {
      format?: undefined;
      parse?: undefined;
    }
  | {
      format: (value: any) => boolean;
      parse: (checked: boolean) => any;
    };

interface FormCheckboxBaseProps extends ComponentPropsWithoutRef<'input'> {
  className?: string;
  label?: string;
  name: string;
  isRequired?: boolean;
  labelRef?: Ref<HTMLLabelElement>;
  validations?: RegisterOptions;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

type FormCheckboxProps = FormCheckboxBaseProps & FormatParsePair;

const FormCheckbox = ({
  className,
  label,
  name,
  isRequired = false,
  labelRef,
  validations,
  onChange,
  format,
  parse,
  ...rest
}: FormCheckboxProps) => {
  const { nonDebugT } = useTranslation();
  const { control } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({ name, control, rules: validations });

  const labelId = `${name}-label`;
  const errorId = `${name}-error`;
  const resolvedLabel = useMemo(
    () => (isRequired ? nonDebugT('Input.Field.Required', label) : label),
    [isRequired, label, nonDebugT],
  );

  const handleOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      const parsedValue = parse ? parse(checked) : checked;
      field.onChange(parsedValue);
      onChange?.(e);
    },
    [field, parse, onChange],
  );

  return (
    <Stack className={className} spacing={4}>
      <Inline noWrap spacing={8}>
        <Checkbox
          {...field}
          aria-errormessage={errorId}
          aria-invalid={Boolean(error)}
          aria-labelledby={`${labelId} ${errorId}`}
          aria-required={isRequired}
          checked={format ? format(field.value) : field.value}
          hasError={Boolean(error)}
          id={name}
          onBlur={field.onBlur}
          onChange={handleOnChange}
          ref={field.ref}
          required={isRequired}
          {...rest}
        />
        <Checkbox.Label htmlFor={name} id={labelId} ref={labelRef}>
          {resolvedLabel}
        </Checkbox.Label>
      </Inline>
      <Input.Meta error={error?.message} errorId={errorId} hasError={Boolean(error)} />
    </Stack>
  );
};

export default FormCheckbox;
